import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LucideIcon } from '@skin-seoul/design-system';
import { ReviewWriteForm } from './ReviewWriteForm';
import { getDefaultConfirmationData } from '@skin-seoul/service/src/utils/review/modalUtils';
import { CompleteView } from './common/CompleteView';
import { ConfirmationDialog } from './common/ConfirmationDialog';
import { COLORS, SPACING, Z_INDEX } from './common/constants';
import { useReviewForm } from '@skin-seoul/service/src/hooks/review/useReviewForm';
import { useReviewModalState } from '@skin-seoul/service/src/hooks/review/useReviewModalState';
import { useReviewSubmission } from '@skin-seoul/service/src/hooks/review/useReviewSubmission';
import type { ReviewModalProps } from '@skin-seoul/service';

export const ReviewWritePcModal = ({
  isOpen,
  productInfo,
  productId,
  onClose,
  onSubmit,
  mode = 'create',
  initialValues,
  existingMedia,
}: ReviewModalProps) => {
  const {
    formState,
    formActions,
    formOptions,
    hasUnsavedChanges,
    isSubmitDisabled,
    validationErrors,
    isMember,
    resetForm,
  } = useReviewForm({
    isOpen,
    mode,
    initialValues,
    existingMedia,
  });

  const { isComplete, showConfirmation, setIsComplete, handleClose, handleConfirmClose, handleCancelClose } =
    useReviewModalState({
      isOpen,
      onClose: () => {
        resetForm();
        onClose();
      },
      hasUnsavedChanges,
      enableConfirmation: true,
    });

  const { handleSubmit, submissionError, isSubmitting } = useReviewSubmission({
    formData: formState,
    onSubmit,
    onSubmissionComplete: () => setIsComplete(true),
  });

  const confirmationData = getDefaultConfirmationData();

  return (
    <>
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center"
            style={{ zIndex: Z_INDEX.modal }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            onClick={handleClose}
          >
            <motion.div
              className="rounded-[24px] max-w-[638px] w-[90vw] max-h-[90dvh] flex flex-col overflow-hidden"
              style={{
                background: COLORS.background,
                boxShadow: `0 8px 32px ${COLORS.shadow}`,
              }}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: 'spring',
                damping: 20,
                stiffness: 300,
                duration: 0.3,
              }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {!isComplete && (
                <div
                  className="flex items-center justify-between"
                  style={{ padding: `${SPACING.xl} ${SPACING.xl} 0px ${SPACING.xl}` }}
                >
                  <p className="text-paragraph-large font-semibold">
                    {mode === 'edit' ? 'Edit Review' : 'Leave a Review'}
                  </p>
                  <button
                    className="bg-transparent border-none p-0 cursor-pointer flex items-center justify-center"
                    onClick={handleClose}
                  >
                    <LucideIcon name="X" width="24px" height="24px" className="text-generic-100" />
                  </button>
                </div>
              )}
              <div
                className="flex-1 overflow-y-auto"
                style={{ padding: `${SPACING.lg} ${SPACING.xl} ${SPACING.xl} ${SPACING.xl}` }}
              >
                {!isComplete ? (
                  <ReviewWriteForm
                    formData={formState}
                    formActions={formActions}
                    options={formOptions}
                    productInfo={productInfo}
                    productId={productId}
                    isSubmitDisabled={isSubmitDisabled}
                    validationErrors={validationErrors}
                    isMember={isMember}
                    onSubmit={handleSubmit}
                    variant="pc"
                    submissionAlertMessage={submissionError?.message ?? null}
                    submissionFlaggedText={submissionError?.flaggedText}
                    mode={mode}
                    isSubmitting={isSubmitting}
                  />
                ) : (
                  <CompleteView onContinue={onClose} />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmationDialog
        isOpen={showConfirmation}
        title={confirmationData.title}
        message={confirmationData.message}
        confirmText={confirmationData.confirmText}
        cancelText={confirmationData.cancelText}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
};
